import React from 'react';
import styled from 'styled-components';
import * as BuilderInfoUI from '../BuilderInfoUI';
import { appRoutes } from '../../../path';
import {
  BuilderMeasureInfo,
  GoToDictionaryAction,
  InfoPopupHeader,
  InfoPopupSchema,
  ShowInReport,
} from '../components';
import { assertNotNil } from '../../../utils/types';
import { useCurrentUrl } from '../../../utils/routing';
import { INamedMeasureBuilderInfoProps } from '../types';
import {
  getUrlForDataSharing,
  SearchParamsDataSharingEnum,
} from '../../../utils/searchParamsDataSharing';
import { useEntityInfoPopoverContext } from '../../EntityInfoPopover/EntityInfoPopoverContext';

const ContentWrapper = styled.div`
  margin-top: 10px;
`;

export default ({
  measure,
  onChangeRoute,
  onExpand,
}: INamedMeasureBuilderInfoProps) => {
  // It's optional by type, but it should always be present to make this component
  // work properly
  assertNotNil(measure?.version);

  const entityInfoPopoverContext = useEntityInfoPopoverContext();

  const { isReportInUrl, isDictionaryInUrl } = useCurrentUrl();

  const { id, schema, version } = measure;
  const { name, description, creator, createdAt, comment } = version;

  const reportUrl = getUrlForDataSharing(appRoutes.report.query, {
    type: SearchParamsDataSharingEnum.NamedMeasure,
    namedMeasureId: id,
    schemaId: schema.id,
  });

  const dictionaryUrl = getUrlForDataSharing(appRoutes.dictionary.measure, {
    type: SearchParamsDataSharingEnum.Dictionary,
    schemaId: schema.id,
    term: name,
  });

  const navigateToReport = () => {
    if (onChangeRoute) {
      onChangeRoute();
    }
  };

  const handleExpand = (event: React.SyntheticEvent) => {
    assertNotNil(onExpand);
    onExpand();
    entityInfoPopoverContext.closePopover(event);
  };

  return (
    <>
      <InfoPopupHeader
        name={name}
        description={description}
        creator={creator}
        createdAt={createdAt}
        editor={version.editor}
        editedAt={version.editedAt}
        comment={comment}
        link={
          !isDictionaryInUrl && dictionaryUrl ? (
            <GoToDictionaryAction dictionaryUrl={dictionaryUrl} />
          ) : null
        }
      />
      <BuilderInfoUI.MainContent>
        <InfoPopupSchema name={measure.schema.name} />
        <ContentWrapper>
          <BuilderMeasureInfo
            measure={measure}
            showLabels={measure.__typename === 'CompoundMeasure'}
          />
        </ContentWrapper>
      </BuilderInfoUI.MainContent>
      <BuilderInfoUI.FooterContainer>
        {onExpand && (
          <BuilderInfoUI.PopupSecondaryButton
            type="button"
            onClick={handleExpand}
            trackContext="measure"
            trackAction="expand"
            size="small"
          >
            Expand
          </BuilderInfoUI.PopupSecondaryButton>
        )}
        {!isReportInUrl && reportUrl && (
          <ShowInReport
            reportUrl={reportUrl}
            onClick={navigateToReport}
            trackContext="measure"
          />
        )}
      </BuilderInfoUI.FooterContainer>
    </>
  );
};
